import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GaugeCircle, ShieldCheck, Loader2, Fingerprint, Lock, ArrowLeft } from "lucide-react";
import { loginWithPasskey, passkeysSupported } from "@/lib/passkeys";

function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return "Si è verificato un errore. Riprova.";
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("cred"); // cred | pin
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ticket, setTicket] = useState("");
  const [pinSet, setPinSet] = useState(true);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pinRef = useRef(null);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (step === "pin" && pinRef.current) pinRef.current.focus();
  }, [step]);

  const submitCred = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      setTicket(data.ticket);
      setPinSet(data.pin_set);
      setPin("");
      setPin2("");
      setStep("pin");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4}$/.test(pin)) return setError("Il PIN deve essere di 4 cifre.");
    if (!pinSet && pin !== pin2) return setError("I due PIN non coincidono.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-pin", { ticket, pin });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doPasskey = async () => {
    setError("");
    setBusy(true);
    try {
      const u = await loginWithPasskey();
      setUser(u);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiError(err) || "Accesso con impronta non riuscito.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  const onlyDigits = (v) => v.replace(/\D/g, "").slice(0, 4);

  return (
    <div className="min-h-screen w-full bg-slate-950 relative flex items-center justify-center p-6">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(37,99,235,0.35), transparent 40%), radial-gradient(circle at 80% 60%, rgba(124,58,237,0.25), transparent 45%)",
        }}
      />
      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex items-center gap-3 text-white">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-600">
            <GaugeCircle className="h-6 w-6" />
          </div>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">ITALIACORSI</p>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Sales Control</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-white p-8 shadow-2xl">
          {step === "cred" && (
            <form onSubmit={submitCred} data-testid="login-cred-form">
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                Accesso riservato
              </h1>
              <p className="mt-2 text-sm text-slate-500">Inserisci le tue credenziali per continuare.</p>

              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                  <Input
                    type="email"
                    autoComplete="username"
                    data-testid="login-email-input"
                    className="mt-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    data-testid="login-password-input"
                    className="mt-1"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              {error && <p className="mt-4 text-sm font-medium text-rose-600" data-testid="login-error">{error}</p>}

              <Button type="submit" disabled={busy} data-testid="login-submit-button" className="mt-6 h-11 w-full gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Continua
              </Button>

              {passkeysSupported() && (
                <>
                  <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                    <span className="h-px flex-1 bg-slate-200" /> oppure <span className="h-px flex-1 bg-slate-200" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={doPasskey}
                    data-testid="login-passkey-button"
                    className="h-11 w-full gap-2 border-slate-300 text-slate-800 hover:bg-slate-50"
                  >
                    <Fingerprint className="h-5 w-5 text-blue-700" />
                    Accedi con impronta / Face ID
                  </Button>
                </>
              )}
            </form>
          )}

          {step === "pin" && (
            <form onSubmit={submitPin} data-testid="login-pin-form">
              <button
                type="button"
                onClick={() => { setStep("cred"); setError(""); }}
                className="mb-3 flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"
                data-testid="pin-back-button"
              >
                <ArrowLeft className="h-3 w-3" /> Indietro
              </button>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                {pinSet ? "Inserisci il PIN" : "Imposta il tuo PIN"}
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                {pinSet
                  ? "Digita il tuo PIN di sicurezza a 4 cifre."
                  : "Scegli un PIN a 4 cifre: ti verrà richiesto ad ogni accesso."}
              </p>

              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">PIN (4 cifre)</Label>
                  <Input
                    ref={pinRef}
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    data-testid="login-pin-input"
                    className="mt-1 tracking-[0.5em] text-center text-lg"
                    value={pin}
                    onChange={(e) => setPin(onlyDigits(e.target.value))}
                    required
                  />
                </div>
                {!pinSet && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Conferma PIN</Label>
                    <Input
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      data-testid="login-pin-confirm-input"
                      className="mt-1 tracking-[0.5em] text-center text-lg"
                      value={pin2}
                      onChange={(e) => setPin2(onlyDigits(e.target.value))}
                      required
                    />
                  </div>
                )}
              </div>

              {error && <p className="mt-4 text-sm font-medium text-rose-600" data-testid="pin-error">{error}</p>}

              <Button type="submit" disabled={busy} data-testid="pin-submit-button" className="mt-6 h-11 w-full gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {pinSet ? "Accedi" : "Imposta PIN e accedi"}
              </Button>
            </form>
          )}

          <div className="mt-6 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
            <span>Accesso protetto. Ogni accesso viene notificato all'amministratore. La registrazione è disabilitata.</span>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} ITALIACORSI — Sales Control
        </p>
      </div>
    </div>
  );
}
